import { Button, Form, InputNumber, Modal, Space, Typography } from "antd";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import State from "../../../../State";
import intl from "../../../../utils/intl";
import { FuelingProcess } from "../../state";
import * as actions from "./actions";
import messages from "./messages";
import FuelingProcessesReceiptEditState from "./state";

export const RightAligned = styled.div`
  text-align: right;
`;

const FuelingProcessReceiptEdit: React.FC = () => {
  const state = useSelector<State, FuelingProcessesReceiptEditState>(
    (state) => state.processReceiptEdit
  );

  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const currency =
    state.fuelingProcess?.details.initialPrice?.currency ?? "RUB";

  const validateAndSave = async () => {
    if (state.fuelingProcess == null) {
      return;
    }

    let fields: {
      totalAmount: number;
      price: number;
      quantity: number;
    };

    try {
      fields = await form.validateFields();
    } catch (e) {
      console.log("Validation error: ", e);
      return;
    }

    const bigDifferenceHints = getTooBigInputDifferencesHints(
      state.fuelingProcess,
      fields.totalAmount,
      fields.price,
      fields.quantity
    );
    let bigDifferenceHint = bigDifferenceHints.join("\n");
    if (bigDifferenceHint !== "") {
      bigDifferenceHint += "\n\n";
    }

    if (
      !confirm(
        `${bigDifferenceHint}${intl.formatMessage(messages.saveConfirm)}`
      )
    ) {
      return;
    }

    dispatch(
      actions.saveFuelingProcessReceipt(
        state.fuelingProcess.details.fuelingProcessId,
        fields.totalAmount,
        fields.price,
        fields.quantity,
        currency
      )
    );
  };

  const onCancel = () => {
    dispatch(actions.hideFuelingProcessReceiptEdit());
  };

  const getErrorMessageDescriptor = (code: string | null) => {
    switch (code) {
      case "rejected":
        return messages.errorCodeRejected;
      case "unacceptableStateTransition":
        return messages.errorCodeUnacceptableStateTransition;
      case "error":
        return messages.errorCodeError;
      default:
        return messages.errorCodeError;
    }
  };

  return (
    <Modal
      width={640}
      onCancel={onCancel}
      footer={null}
      destroyOnClose={false}
      afterClose={() => form.resetFields()}
      visible={state.isModalVisible}
    >
      <Typography.Title level={4}>
        {intl.formatMessage(messages.title)}
      </Typography.Title>
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFinish={validateAndSave}
      >
        <Form.Item
          label={intl.formatMessage(messages.totalAmount)}
          name="totalAmount"
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.pleaseInputTotalAmount),
            },
            {
              type: "number",
              min: 0.01,
              message: intl.formatMessage(messages.mustBePositive),
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            precision={2}
            min={0}
            addonAfter={currency}
            disabled={state.isSaving}
            placeholder="0.00"
          />
        </Form.Item>

        <Form.Item
          label={intl.formatMessage(messages.pricePerLiter)}
          name="price"
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.pleaseInputPricePerLiter),
            },
            {
              type: "number",
              min: 0.01,
              message: intl.formatMessage(messages.mustBePositive),
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            precision={2}
            min={0}
            addonAfter={currency}
            disabled={state.isSaving}
            placeholder="0.00"
          />
        </Form.Item>

        <Form.Item
          label={intl.formatMessage(messages.quantity)}
          name="quantity"
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.pleaseInputQuantity),
            },
            {
              type: "number",
              min: 0.01,
              message: intl.formatMessage(messages.mustBePositive),
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            precision={2}
            min={0}
            addonAfter={intl.formatMessage(messages.litersLabel)}
            disabled={state.isSaving}
            placeholder="0.00"
          />
        </Form.Item>

        <Form.Item>
          <RightAligned>
            <Space>
              <Button
                id="saveReceiptBtn"
                type="primary"
                htmlType="submit"
                loading={state.isSaving}
                disabled={state.isSaving}
              >
                {!state.isSaving
                  ? `${intl.formatMessage(messages.save)}*`
                  : `${intl.formatMessage(messages.saving)}`}
              </Button>
              <Button onClick={onCancel} disabled={state.isSaving}>
                {intl.formatMessage(messages.cancel)}
              </Button>
            </Space>
          </RightAligned>
        </Form.Item>

        <Form.Item>
          <div>
            <Typography.Text type="secondary">
              *{intl.formatMessage(messages.eventualUpdateDescription)}
            </Typography.Text>
          </div>
          {state.saveErrorCode && (
            <Typography.Text type="danger">
              {intl.formatMessage(
                getErrorMessageDescriptor(state.saveErrorCode)
              )}
              : {state.saveErrorMessage}
            </Typography.Text>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export function getTooBigInputDifferencesHints(
  fuelingProcess: FuelingProcess,
  totalAmount: number,
  price: number,
  quantity: number
): string[] {
  const bigDifferenceHints: string[] = [];

  const initialPrice = fuelingProcess.details.initialPrice?.amount;
  const partnerInitialPrice =
    fuelingProcess.details.partnerInitialPrice?.amount;

  if (initialPrice && partnerInitialPrice) {
    const expectedQuantity =
      fuelingProcess.details.requestedAmount.amount / partnerInitialPrice;
    const lowerQuantityBorder = expectedQuantity / 2;
    const upperQuantityBorder = expectedQuantity * 2;

    const lowerPriceBorder = initialPrice / 2;
    const upperPriceBorder = initialPrice * 2;

    const lowerTotalAmountBorder = expectedQuantity * lowerPriceBorder;
    const upperTotalAmountBorder = expectedQuantity * upperPriceBorder;

    if (
      totalAmount < lowerTotalAmountBorder ||
      totalAmount > upperTotalAmountBorder
    ) {
      bigDifferenceHints.push(
        intl.formatMessage(messages.totalAmountBigDifferenceHint)
      );
    }

    if (price < lowerPriceBorder || price > upperPriceBorder) {
      bigDifferenceHints.push(
        intl.formatMessage(messages.priceBigDifferenceHint)
      );
    }

    if (quantity < lowerQuantityBorder || quantity > upperQuantityBorder) {
      bigDifferenceHints.push(
        intl.formatMessage(messages.quantityBigDifferenceHint)
      );
    }
  }

  return bigDifferenceHints;
}

export default FuelingProcessReceiptEdit;
